import omit from 'lodash/omit';
import { NextRouter } from 'next/router';
import { IOffering, IOrder, OrderStatus } from 'stores/networkInterfaces';
import { reportEvent } from 'utils/analytics';
import { getOrderBasePath, orderTypeMap } from 'utils/navigateUtils';
import { getSingleValue } from 'utils/urlUtils';

export const DEFAULT_INVEST_AMOUNT = 50000;
export const MIN_INVEST_AMOUNT = 25000;
export const MAX_INVEST_AMOUNT = 1000000;

export const BUYER_FEE_PERCENT = 0.01;
export const SELLER_FEE_PERCENT = 0.03;

export const STEPS = {
  details: 'details',
  review: 'review',
  summary: 'summary'
};

export const updateOrderPath = (
  router: NextRouter,
  {
    offeringId,
    orderType,
    orderId,
    step
  }: {
    offeringId: string;
    orderType: number;
    orderId?: string;
    step?: string;
  }
) => {
  const newPath = getOrderBasePath(offeringId, orderType, step);
  const currentPath = router?.asPath?.split('?')[0];
  const currentOrderId = getSingleValue(router?.query?.oid);
  if (currentPath === newPath && orderId === currentOrderId) {
    return;
  }

  reportOrderEvent(offeringId, orderType, orderId ?? currentOrderId, step);

  router?.replace(
    {
      pathname: newPath,
      query: {
        ...omit(router?.query, ['all', 'event']),
        ...((orderId ?? currentOrderId) && { oid: orderId ?? currentOrderId })
      }
    },
    undefined,
    { shallow: true }
  );
};

export const reportOrderEvent = (
  offeringId?: string,
  orderType?: number,
  orderId?: string,
  step?: string
) => {
  if (!offeringId || !orderId || orderType == undefined) {
    return;
  }

  const orderTypeStr = orderTypeMap[orderType];
  const orderEvent = step ? `${orderTypeStr}_order_${step}` : `${orderTypeStr}_order_start`;
  const orderEventData = {
    offering_id: offeringId,
    order_type: orderTypeStr,
    order_id: orderId,
    step: step
  };

  reportEvent(orderEvent, orderEventData);
};

export const reportTradeEvent = (offeringId?: string, tradeId?: string, step?: string) => {
  if (!offeringId || !tradeId) {
    return;
  }

  const tradeEvent = step ? `trade_${step}` : `trade_start`;
  const tradeEventData = {
    offering_id: offeringId,
    trade_id: tradeId,
    step: step
  };

  reportEvent(tradeEvent, tradeEventData);
};

export const calculateSecondaryOrderStep = (
  router: NextRouter,
  order?: IOrder,
  afterDocSigned?: boolean,
  showOrderDetails?: boolean,
  editMode?: boolean
) => {
  if (order && !editMode && (!order?.document_id || order.order_status != OrderStatus.pending)) {
    return STEPS.summary;
  }

  if (order && !editMode && (!order?.document_id || order?.document_sign_date || afterDocSigned)) {
    return STEPS.summary;
  }

  if (showOrderDetails || editMode) {
    return STEPS.details;
  }

  if (order) {
    return STEPS.review;
  }

  const pathParts = router?.asPath?.split('?')[0]?.split('/');
  if (pathParts.length > 4) return pathParts[4];
};

export const calculatePrimaryOrderStep = (
  router: NextRouter,
  order?: IOrder,
  showOrderDetails?: boolean,
  editMode?: boolean
) => {
  if (order && !editMode) {
    return STEPS.summary;
  }

  if (showOrderDetails || editMode) {
    return STEPS.details;
  }

  const pathParts = router?.asPath?.split('?')[0]?.split('/');
  if (pathParts.length > 4) return pathParts[4];
};

export const calculatePremium = (offeringPrice?: number, orderPrice?: number) => {
  return offeringPrice && orderPrice && orderPrice / offeringPrice - 1.0;
};

export const isOrderValid = (sum: number, minAmount: number) => sum >= minAmount;

export const getDefaultInvestAmount = (offering?: IOffering) => {
  return Math.min(
    Math.max(getMinInvestAmount(offering), DEFAULT_INVEST_AMOUNT),
    getMaxInvestAmount(offering)
  );
};

export const getMinInvestAmount = (offering?: IOffering) => {
  return offering?.min_invest_amount || MIN_INVEST_AMOUNT;
};

export const getMaxInvestAmount = (offering?: IOffering) => {
  return offering?.max_invest_amount || MAX_INVEST_AMOUNT;
};

export const getBuyerWaivedFees = (offering?: IOffering) => {
  return Math.max(
    BUYER_FEE_PERCENT -
      (offering?.buyer_fees_investor ?? offering?.buyer_fees ?? BUYER_FEE_PERCENT),
    0
  );
};

export const getSellerWaivedFees = (offering?: IOffering) => {
  return Math.max(
    SELLER_FEE_PERCENT -
      (offering?.seller_fees_investor ?? offering?.seller_fees ?? SELLER_FEE_PERCENT),
    0
  );
};

export const calcQuantity = (total: number, share: number) => Math.floor(total / share);
